import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  makeStyles,
  Box,
  LinearProgress,
  styled,
} from "@material-ui/core";
import { linearProgressClasses } from "@mui/material";
import { School } from "@material-ui/icons";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles(theme => ({
  statusCard: {
    height: "100%",
    borderRadius: theme.spacing(1),
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
  },
  statusTitle: {
    display: "flex",
    alignItems: "center",
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
  },
  statusIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  errorCard: {
    minHeight: 200,
    border: "2px solid",
    borderColor: "#ec4d1f",
    borderRadius: theme.spacing(1),
  },
}));

/**
 * A component to display an error message when the university status
 * could not be retrieved. It renders a Material UI Card with a
 * heading and a paragraph of text.
 *
 * @returns {React.ReactElement} A JSX element representing the Card.
 */
function ErrorCard() {
  const classes = useStyles();

  return (
    <Card className={classes.errorCard}>
      <CardContent>
        <Typography
          variant="h6"
          component="div"
          className={classes.statusTitle}
        >
          <School color="error" style={{ marginRight: 8 }} /> Error
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Error retrieving university status - please refresh the page
        </Typography>
      </CardContent>
    </Card>
  );
}

/**
 * A component that displays the university status of a user within a styled card.
 * If the university status cannot be retrieved, it displays an error card.
 * Otherwise, it shows the progress of relevant and compulsory modules.
 * Provides a button to continue training if the status is above a certain threshold
 * and the profile is not of another user.
 *
 * @param {object} props - Component props
 * @param {boolean} [props.otherProfile=false] - Indicates if the profile displayed is of another user.
 * @param {object} props.universityStatus - The status of the university modules.
 * @returns {React.ReactElement} A JSX element representing the university status card.
 */
function UniversityTile({ otherProfile = false, universityStatus }) {
  const classes = useStyles();
  const history = useHistory();

  const statusColor = {
    0: "#10284b",
    101: "#3bc3a9",
    102: "#ff9a2b",
  };
  const BorderLinearProgress = styled(LinearProgress)(() => ({
    height: 13,
    borderRadius: 5,
    width: "94%",
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: "#D3D3D3",
    },
    [`& .${linearProgressClasses.bar}`]: {
      borderRadius: 5,
      backgroundColor: statusColor[102],
    },
  }));
  // Calculate percentage for progress bars
  const getRelevantModulesProgress = () => {
    return (
      (universityStatus.passedRelevantModules /
        universityStatus.relevantModules) *
      100
    );
  };

  const getCompulsoryModulesProgress = () => {
    return (
      (universityStatus.passedCompulsoryModules /
        universityStatus.compulsoryModules) *
      100
    );
  };

  return (
    <Grid item xs={12} sm={6}>
      {universityStatus.status < 0 ? (
        <ErrorCard />
      ) : (
        <Card
          className={classes.statusCard}
          style={{
            border: `2px solid ${statusColor[universityStatus.status]}`,
          }}
        >
          <CardContent>
            <Typography variant="h6" className={classes.statusTitle}>
              <School
                className={classes.statusIcon}
                style={{ color: "#ff9a2b" }}
              />
              Zawadi University Status
            </Typography>

            <Box style={{ padding: "0 3%" }}>
              <Typography variant="subtitle2" color="textSecondary">
                Track your academic progress
              </Typography>
              <Typography variant="body1" style={{ marginTop: 6 }}>
                Relevant Modules
              </Typography>
              <Box display="flex" alignItems="center">
                <BorderLinearProgress
                  variant="determinate"
                  value={`${getRelevantModulesProgress()}`}
                />

                <Typography
                  variant="body2"
                  fontWeight="bold"
                  style={{ paddingLeft: 8 }}
                >
                  {universityStatus.passedRelevantModules}/
                  {universityStatus.relevantModules}
                </Typography>
              </Box>
            </Box>

            <Box mt={2} style={{ padding: "0 3%" }}>
              <Typography variant="body1">Compulsory Modules</Typography>
              <Box display="flex" alignItems="center">
                <BorderLinearProgress
                  variant="determinate"
                  value={`${getCompulsoryModulesProgress()}`}
                />

                <Typography
                  variant="body2"
                  fontWeight="bold"
                  style={{ paddingLeft: 8 }}
                >
                  {universityStatus.passedCompulsoryModules}/
                  {universityStatus.compulsoryModules}
                </Typography>
              </Box>
            </Box>

            {!otherProfile && universityStatus.status > 100 && (
              <Box mt={4} style={{ padding: "0 2%" }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  style={{
                    textTransform: "none",
                  }}
                  onClick={() => {
                    history.push("/training");
                  }}
                >
                  Continue Training
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Grid>
  );
}

export default UniversityTile;
